import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import type { Delivery } from '@interfaces/ILogistics';
import { arrivalVerdict, VERDICT_TONE_CLASS, isOverdue, overdueDays, type DragData } from '../board.logic';
import { formatDay, zonedDay } from '../logistics.format';
import { CardFace, SupplierLine } from './BoardCardLines';
import { CriticalStar } from './CriticalStar';
import styles from './DeliveryBoard.module.css';

/**
 * What an ARRIVED card shows in place of the negotiation trail: a fixed
 * four-row list, Requested / Replied by / ETA / Arrived, always all four,
 * in that order — a short, scannable summary of the whole delivery now that
 * it's settled, ending on the fact it's really FOR. Arrived is the one row
 * that earns colour and carries the on-time/early/overdue verdict: it is the
 * only date here that is a settled FACT rather than an ask or a reply
 * (ETA is only ever an agreement), and it is what a Delivered-column
 * card is actually scanned for — the verdict belongs next to the thing it's
 * a verdict ABOUT. Requested, Replied by and ETA each show a bare '—' when
 * unset — the same locale-neutral placeholder the stock and order dialogs
 * already use for "nothing here yet" — since a settled card can genuinely
 * have skipped a step (e.g. arrived with no reply, or no ETA was ever
 * agreed).
 *
 * This also replaces `renderCardBody`'s ordinary due-date footer for arrived
 * cards: once ETA owns that information, the footer's "Due {date}" /
 * "Estimated" / overdue-badge block would just duplicate it in a second,
 * un-labelled spot on the same card — see the `!d.deliveredAt` gate there.
 *
 * In-flight cards do NOT use this: `renderTrail` below keeps the shorter
 * chip trail for those, since most of this list would be blank on a card
 * that hasn't landed yet.
 */
function renderArrival(
  d: Delivery,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
  tz: string,
): ReactNode {
  // deliveredAt is an ISO instant, not a calendar day — convert to the day it
  // fell on in the PROJECT's zone before formatting or comparing. The detail
  // drawer calls the same helper, so the row and the drawer can never disagree.
  const arrivedDay = zonedDay(d.deliveredAt!, tz);
  const verdict = arrivalVerdict(d.dueDate, arrivedDay, t);

  const rows: Array<{
    testId: string;
    label: string;
    value: string;
    tone?: string;
    qualifier?: ReactNode;
  }> = [
    {
      testId: 'date-requested',
      label: t('logistics.pipeline.requested'),
      value: d.requestedDate ? formatDay(d.requestedDate, lang) : '—',
    },
    {
      testId: 'date-answered',
      label: t('logistics.pipeline.answered'),
      value: d.answeredDate ? formatDay(d.answeredDate, lang) : '—',
    },
    {
      testId: 'date-due',
      label: t('logistics.pipeline.wasDue'),
      value: d.dueDate ? formatDay(d.dueDate, lang) : '—',
    },
    {
      testId: 'date-arrived',
      label: t('logistics.pipeline.arrival.label'),
      value: formatDay(arrivedDay, lang),
      // The date carries the verdict, not just the qualifier beside it: an
      // arrival that landed LATE is not good news, so it must not read green.
      // Red for overdue (matches the "(Nd overdue)" qualifier); the settled
      // green stays for on-time/early or an arrival with no due date to miss.
      tone: verdict?.tone === 'late' ? styles.varLate : styles.dateArrived,
      // Parenthesised, literal punctuation rather than baked into the i18n
      // string — same call as the footer's " · Estimated" qualifier below,
      // which keeps the translated variance text reusable on its own. Sits
      // on Arrived, not ETA: the verdict is about the arrival, and this
      // is the row a Delivered-column card is actually scanned for.
      qualifier: verdict ? (
        <i className={`${styles.variance} ${styles[VERDICT_TONE_CLASS[verdict.tone]]}`}>({verdict.text})</i>
      ) : null,
    },
  ];

  return (
    // <span>, not <div> — same <button> nesting constraint as the trail below.
    <span className={styles.dateList}>
      {rows.map((row) => (
        <span key={row.testId} className={styles.dateRow} data-testid={row.testId}>
          <span className={styles.dateRowLabel}>{row.label}</span>
          <b className={row.tone ? `${styles.dateRowValue} ${row.tone}` : styles.dateRowValue}>
            {row.value}
          </b>
          {row.qualifier}
        </span>
      ))}
    </span>
  );
}

/**
 * The negotiation trail: a filled chip per date the supplier actually gave —
 * requested, answered — plus a dashed ghost chip standing in for whichever of
 * those two the card is still waiting on. Once the delivery has landed the
 * history is settled enough to earn the fuller `renderArrival` summary
 * instead — there both dates plus the arrival matter at a glance; here, only
 * the one still-open question does.
 *
 * The trail still says NOTHING about the delivery's *status*. The board's
 * columns are the status the user sees and names; a card narrating Genba's
 * parallel workflow underneath (a red "Declined — needs a new request" chip)
 * put two status vocabularies on one card, the second of them from a system
 * the board deliberately hides. That vocabulary is gone from the type itself
 * now, and the gating below reads `deliveredAt` — the arrival FACT — which is
 * all "has it landed" ever meant. The ghost only ever names the DATE step
 * still awaited (Requested / Answer) — there is deliberately no third
 * "Confirm" ghost once both dates are in.
 */
function renderTrail(
  d: Delivery,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
  tz: string,
): ReactNode {
  if (d.deliveredAt) return renderArrival(d, t, lang, tz);

  const chips: ReactNode[] = [];
  if (d.requestedDate) {
    chips.push(
      <span key="req" className={styles.dateChip}>
        <span className={styles.dateLabel}>{t('logistics.pipeline.requested')}</span>
        <b>{formatDay(d.requestedDate, lang)}</b>
      </span>,
    );
  } else {
    chips.push(
      <span key="req-ghost" className={`${styles.dateChip} ${styles.chipGhost}`}>
        <span className={styles.dateLabel}>{t('logistics.pipeline.requested')}</span>
        <i className={styles.ghostHint}>{t('logistics.pipeline.ghost.pending')}</i>
      </span>,
    );
  }
  if (d.answeredDate) {
    chips.push(
      <span key="ans" className={styles.dateChip}>
        <span className={styles.dateLabel}>{t('logistics.pipeline.answered')}</span>
        <b>{formatDay(d.answeredDate, lang)}</b>
      </span>,
    );
  } else if (d.requestedDate) {
    // Only awaited once a request has actually gone out — with no requested
    // date yet, the "Requested" ghost above already says the whole trail is
    // waiting, and a second ghost here would repeat it.
    chips.push(
      <span key="ans-ghost" className={`${styles.dateChip} ${styles.chipGhost}`}>
        <span className={styles.dateLabel}>{t('logistics.pipeline.answered')}</span>
        <i className={styles.ghostHint}>{t('logistics.pipeline.ghost.pending')}</i>
      </span>,
    );
  }

  return (
    // <span>, not <div>: the trail renders inside `.cardMain`, which is a
    // <button>, and a <div> there is invalid nesting.
    <span className={styles.dates}>
      {chips.map((chip, i) => (
        <Fragment key={i}>
          {i > 0 ? (
            <span className={styles.chipArrow} aria-hidden="true">
              →
            </span>
          ) : null}
          {chip}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Everything a card SAYS, independent of how it's mounted — shared between
 * the real, interactive card and its `DragOverlay` ghost (`BoardCardPreview`
 * below), so a drag preview can never silently drift from what the column
 * itself renders. No hooks of its own beyond what the caller passes in:
 * `useDraggable` lives only in `BoardCard`, since a `DragOverlay` item must
 * NOT register a second draggable under the same id while the original is
 * still mid-drag.
 */
function renderCardBody(
  d: Delivery,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
  tz: string,
  now: Date,
  /** The order's supplier name, or null for none — one muted line under
   *  the title. No phone here; the drawer owns contact details. */
  supplierName: string | null = null,
): ReactNode {
  const late = isOverdue(d, now, tz);
  // Every source the face speaks for, so the pill's tooltip lists the tasks
  // per line the way the design asks ("Rebar D16 · Formwork 3F").
  const sources = d.lines.flatMap((line) => line.sources);
  return (
    <>
      <CardFace delivery={d} sources={sources} t={t} />
      {supplierName ? <SupplierLine name={supplierName} /> : null}
      {d.deliveryOrder ? <span className={styles.meta}>{d.deliveryOrder}</span> : null}
      {renderTrail(d, t, lang, tz)}
      {/* Once a card has arrived, `renderArrival`'s ETA row (inside the
          trail above) owns the due-date story — on time / early / overdue,
          worked out against the SAME due date this footer would otherwise
          repeat underneath it. Rendering both would fragment one fact across
          two unlabelled blocks on the same card, so the whole footer —
          confirmed, estimated, and duePending branches alike — is gated off
          the moment `deliveredAt` is set. Everything below is unchanged for
          in-flight cards. */}
      {d.deliveredAt ? null : d.dueDate ? (
        <span className={styles.cardFoot}>
          <span className={late ? `${styles.due} ${styles.dueLate}` : styles.due}>
            {/* The supplier hasn't answered yet, so this date is Genba's
                ask rather than a confirmed one — say so rather than let it
                read as settled. Once an answer lands (or there was never a
                request to begin with) it's the plain due text. The
                qualifier is an <i> like the awaited hints, but inherits the
                due line's own color — it qualifies the date, it doesn't
                await anything. */}
            {t('logistics.pipeline.due', { d: formatDay(d.dueDate, lang) })}
            {d.requestedDate && !d.answeredDate ? (
              <> · <i>{t('logistics.pipeline.estimated')}</i></>
            ) : null}
          </span>
          {late ? (
            <span className={styles.overdueBadge}>
              {t('logistics.board.overdueBadge', { n: overdueDays(d, now, tz) })}
            </span>
          ) : null}
        </span>
      ) : d.answeredDate ? (
        /* The supplier answered but named no date, so the delivery has no
           deadline yet. An empty footer here reads identically to a card
           nobody has touched — this says which of the two it is. Never late:
           there is no date to be late against. The label stays the muted
           `.duePending` tone; only the "pending" word carries the amber
           awaited-tone, the same `.ghostHint` the trail's ghost chips use. */
        <span className={styles.cardFoot}>
          <span className={styles.duePending}>{t('logistics.pipeline.duePendingLabel')}</span>
          {/* <i> like the trail's ghost hints — italic is part of the
              awaited-tone grammar; the size class keeps it on the label's
              baseline instead of inheriting the card's base size. */}
          <i className={`${styles.ghostHint} ${styles.duePendingHint}`}>
            {t('logistics.pipeline.ghost.pending')}
          </i>
        </span>
      ) : null}
    </>
  );
}

/**
 * The floating drag ghost, rendered inside `DeliveryBoard`'s `<DragOverlay>`
 * — the ONE piece of a card drag that escapes every column's stacking
 * context (dnd-kit portals it to the end of `document.body`), which is what
 * keeps the dragged card visible on top of the column it's passing over
 * instead of sliding behind it. Deliberately inert: no drag hookup (the
 * original `BoardCard` still owns that; this is a second, non-draggable
 * copy), no kebab menu, no click handler — nothing here is reachable while
 * it's mid-flight.
 */
export function BoardCardPreview({
  delivery: d,
  tz,
  now,
  supplierName = null,
  onHover = false,
}: {
  delivery: Delivery;
  tz: string;
  now: Date;
  supplierName?: string | null;
  /** Read in a hover popup rather than carried by a drag: board width, no grab cursor. */
  onHover?: boolean;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className={`${styles.card} ${onHover ? styles.cardHoverPreview : styles.cardPreview}`}>
      <div className={styles.cardMain}>{renderCardBody(d, t, i18n.language, tz, now, supplierName)}</div>
      <CriticalStar className={styles.cardStar} critical={d.isCritical} />
    </div>
  );
}

export interface IBoardCardProps {
  delivery: Delivery;
  /** The PROJECT's IANA timezone — see logistics.format.zonedDay. */
  tz: string;
  now: Date;
  /** Resolved once by the board; null when the order has no (known) supplier. */
  supplierName?: string | null;
  /** True while this card's delivery is mid-delete — fades under an overlay. */
  deleting: boolean;
  /** True between a cross-column drop and the deliveries prop reflecting it —
   *  keeps the drag fade on so the card can't flash back at full opacity in
   *  its OLD column for the frame before the optimistic move lands. */
  dropPending: boolean;
  /** Non-null while this card is the flash target (jump-to from elsewhere on
   *  the board); carries a nonce so a repeat jump replays the animation. */
  flash: { nonce: number } | null;
  onFlashEnd: () => void;
  onOpenDetail: (d: Delivery) => void;
  /** Stars or unstars the order; absent, the star only reads. */
  onToggleCritical?: (d: Delivery) => void;
  /** Lets the owning board keep a ref map for scroll-to-card, same idiom as
   *  DeliveryPipeline's `rows` map. */
  registerNode: (ref: string, el: HTMLElement | null) => void;
}

export function BoardCard({
  delivery: d,
  tz,
  now,
  supplierName = null,
  deleting,
  dropPending,
  flash,
  onFlashEnd,
  onOpenDetail,
  onToggleCritical,
  registerNode,
}: IBoardCardProps) {
  const { t, i18n } = useTranslation();
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes: { 'aria-describedby': ariaDescribedBy },
    listeners,
    isDragging,
  } = useDraggable({
    id: d.ref,
    data: { type: 'card', ref: d.ref } satisfies DragData,
    // A synced card's status is frozen server-side — don't even offer the drag.
    disabled: deleting || d.isSynced,
  });

  /* No pointer transform here, ever: the DragOverlay ghost in DeliveryBoard is
     the one thing that moves with the cursor. Translating the original too sent
     a second, faded copy of the card travelling under the ghost — and on
     release that copy visibly snapped home before the move landed. Mid-drag,
     this card's whole job is to sit still in its column as the faded
     placeholder; `dropPending` then holds that fade through the frame between
     the drop and the deliveries prop catching up. */
  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        // The activator is the (unfocusable) root itself, so the keyboard
        // sensor ignores an Enter/Space bubbling up from the card's buttons —
        // that keydown is a click, not a pick-up.
        setActivatorNodeRef(el);
        registerNode(d.ref, el);
      }}
      className={styles.card}
      style={{ opacity: isDragging || dropPending ? 0.4 : 1 }}
      data-deleting={deleting || undefined}
      aria-describedby={ariaDescribedBy}
      {...listeners}
    >
      {flash ? (
        <span key={flash.nonce} className={styles.jumpFlash} aria-hidden="true" onAnimationEnd={onFlashEnd} />
      ) : null}
      {deleting ? (
        <span className={styles.deletingOverlay} role="status">
          {t('logistics.pipeline.deleting')}
        </span>
      ) : null}
      {/* The card's only affordance: open the detail drawer. Edit and delete
          moved there (2026-08-20) — the hover-revealed kebab that used to sit
          in the corner offered the same two actions one hop earlier, at the
          cost of a second click target on every card. */}
      <button type="button" className={styles.cardMain} onClick={() => onOpenDetail(d)}>
        {renderCardBody(d, t, i18n.language, tz, now, supplierName)}
      </button>
      {/* Beside the open button, never inside it: buttons don't nest. */}
      <CriticalStar className={styles.cardStar} critical={d.isCritical} onToggle={onToggleCritical && (() => onToggleCritical(d))} />
    </div>
  );
}
